import * as Sentry from '@sentry/react';
import './styles/index.css';
import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import { NotFoundRouteComponent } from './components/not-found-route-component';
import {
  scrubOAuthParams,
  stripOAuthParamsBeforeTelemetryInit,
} from '@/utils/scrub-oauth-params';

// Must run before Sentry.init: breadcrumbsIntegration wraps history and reads
// window.location.href before the original call, so a later strip is itself
// captured as a breadcrumb carrying the code.
stripOAuthParamsBeforeTelemetryInit();

Sentry.init({
  dsn: 'https://da140df383334291244b10b1f4ff0ad0@o4509688705581056.ingest.de.sentry.io/4509688858542160',
  integrations: [Sentry.browserTracingIntegration()],

  // The /mobile-auth redirect carries an OAuth code in its query string, which
  // browserTracingIntegration would otherwise record as `request.url` on the
  // pageload transaction.
  // Defence in depth: breadcrumbs are attached during prepareEvent, ahead of
  // beforeSend, so the request.url hooks below never see them.
  beforeBreadcrumb: (breadcrumb) => {
    const data = breadcrumb.data;
    if (data) {
      if (typeof data.from === 'string') data.from = scrubOAuthParams(data.from);
      if (typeof data.to === 'string') data.to = scrubOAuthParams(data.to);
    }
    return breadcrumb;
  },
  beforeSend: (event) => {
    if (event.request?.url) event.request.url = scrubOAuthParams(event.request.url);
    return event;
  },
  beforeSendTransaction: (event) => {
    if (event.request?.url) event.request.url = scrubOAuthParams(event.request.url);
    return event;
  },

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 0.1,
  // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    /^https:\/\/dashboard\.ipractice\.nl\/api/,
    /^https:\/\/dashboard\.ipractice-test\.nl\/api/,
  ],

  debug: process.env.NODE_ENV !== 'production',

  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: false,

  // Configure Session Replay
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1,
});

const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFoundRouteComponent,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('root')!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement, {
    onUncaughtError: Sentry.reactErrorHandler(),
    onCaughtError: Sentry.reactErrorHandler(),
    onRecoverableError: Sentry.reactErrorHandler(),
  });
  root.render(
    <StrictMode>
      <RouterProvider router={router} />
    </StrictMode>
  );
}
